import { Box } from '@mui/material'
import { FunctionComponent, useEffect, useCallback } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { IScreenLabel } from '../../../shared/services/screen-labels/screen-labels.model'
import { defaultPluralMap, defaultSingularMap } from '../../../context/screen-labels/screen-labels.model'
import { capitaliseFirst } from '../../../shared/utils/misc-shared-utils'
import { FormTextDoubleInput } from '../form-field-components/new-and-edit/form-text-double-input'

export const ScreenLabelForm: FunctionComponent<{
  submitHandler: React.Dispatch<React.SetStateAction<any>>,
  editField: string,
  setEditField: React.Dispatch<React.SetStateAction<any>>,
  screenLabels?: IScreenLabel[]
}> = ({
  submitHandler,
  editField,
  setEditField,
  screenLabels = []
}) => {

    const getInitialValues = useCallback(() => {
      const terms = ['organisation', 'project', 'subsidiary', 'location', 'vehicle', 'supplier', 'product'] as const;
      return terms.reduce((acc, term) => {
        const match = screenLabels.find(item => item.gaiaTerm === term);
        acc[`${term}Singular`] = capitaliseFirst(match?.customTermSingular || '');
        acc[`${term}Plural`] = capitaliseFirst(match?.customTermPlural || '');
        return acc;
      }, {} as { [key: string]: string });
    }, [screenLabels]);

    //Formik
    const screenLabelSchema = Yup.object()
      .shape({
        organisationSingular: Yup.string().trim(),
        organisationPlural: Yup.string().trim(),
        projectSingular: Yup.string().trim(),
        projectPlural: Yup.string().trim(),
        subsidiarySingular: Yup.string().trim(),
        subsidiaryPlural: Yup.string().trim(),
        locationSingular: Yup.string().trim(),
        locationPlural: Yup.string().trim(),
        vehicleSingular: Yup.string().trim(),
        vehiclePlural: Yup.string().trim(),
        supplierSingular: Yup.string().trim(),
        supplierPlural: Yup.string().trim(),
        emissionSingular: Yup.string().trim(),
        emissionPlural: Yup.string().trim(),
        productSingular: Yup.string().trim(),
        productPlural: Yup.string().trim(),
      })
      .test('pairs-complete', function (values) {
        const { createError } = this;
        if (!values) return true;
        const pairs: Array<[keyof typeof values, keyof typeof values]> = [
          ['organisationSingular', 'organisationPlural'],
          ['projectSingular', 'projectPlural'],
          ['subsidiarySingular', 'subsidiaryPlural'],
          ['locationSingular', 'locationPlural'],
          ['vehicleSingular', 'vehiclePlural'],
          ['supplierSingular', 'supplierPlural'],
          ['emissionSingular', 'emissionPlural'],
          ['productSingular', 'productPlural'],
        ];
        for (const [singKey, plurKey] of pairs) {
          const singular = (values as any)[singKey];
          const plural = (values as any)[plurKey];
          const hasSingular = typeof singular === 'string' && singular.trim().length > 0;
          const hasPlural = typeof plural === 'string' && plural.trim().length > 0;
          if (hasSingular !== hasPlural) {
            const missingKey = hasSingular ? (plurKey as string) : (singKey as string);
            return createError({ path: missingKey, message: `The ${hasSingular ? 'plural' : 'singular'} is required` });
          }
        }
        return true;
      });

    const formik = useFormik({
      initialValues: getInitialValues(),
      validationSchema: screenLabelSchema,
      onSubmit: (values) => {
        setEditField('');
        const processedValues = Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            value === '' ? null : value
          ])
        );
        submitHandler(processedValues);
      }
    });

    useEffect(() => {
      const nextValues = getInitialValues();
      if (JSON.stringify(formik.values) !== JSON.stringify(nextValues)) {
        formik.setValues(nextValues);
      }
    }, [getInitialValues]);


    const cancelEditHandler = () => {
      setEditField('')
    }

    const handleCancelWithName = (name: string) => {
      const nextValues = getInitialValues();
      if (name.endsWith('Singular')) {
        const base = name.replace('Singular', '');
        formik.setFieldValue(name, nextValues[name]);
        formik.setFieldValue(`${base}Plural`, nextValues[`${base}Plural`]);
      } else if (name.endsWith('Plural')) {
        const base = name.replace('Plural', '');
        formik.setFieldValue(name, nextValues[name]);
        formik.setFieldValue(`${base}Singular`, nextValues[`${base}Singular`]);
      } else {
        formik.setFieldValue(name, nextValues[name]);
      }
      setEditField('');
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Organisation'}
          firstName={'organisationSingular'}
          secondName={'organisationPlural'}
          firstValue={(formik.values as any)['organisationSingular']}
          secondValue={(formik.values as any)['organisationPlural']}
          firstTouched={(formik.touched as any)['organisationSingular']}
          secondTouched={(formik.touched as any)['organisationPlural']}
          firstErrors={(formik.errors as any)['organisationSingular']}
          secondErrors={(formik.errors as any)['organisationPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.organisation)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.organisation])}`}
          isTopRow={true}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Subsidiary'}
          firstName={'subsidiarySingular'}
          secondName={'subsidiaryPlural'}
          firstValue={(formik.values as any)['subsidiarySingular']}
          secondValue={(formik.values as any)['subsidiaryPlural']}
          firstTouched={(formik.touched as any)['subsidiarySingular']}
          secondTouched={(formik.touched as any)['subsidiaryPlural']}
          firstErrors={(formik.errors as any)['subsidiarySingular']}
          secondErrors={(formik.errors as any)['subsidiaryPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.subsidiary)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.subsidiary])}`}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Location'}
          firstName={'locationSingular'}
          secondName={'locationPlural'}
          firstValue={(formik.values as any)['locationSingular']}
          secondValue={(formik.values as any)['locationPlural']}
          firstTouched={(formik.touched as any)['locationSingular']}
          secondTouched={(formik.touched as any)['locationPlural']}
          firstErrors={(formik.errors as any)['locationSingular']}
          secondErrors={(formik.errors as any)['locationPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.location)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.location])}`}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Vehicle'}
          firstName={'vehicleSingular'}
          secondName={'vehiclePlural'}
          firstValue={(formik.values as any)['vehicleSingular']}
          secondValue={(formik.values as any)['vehiclePlural']}
          firstTouched={(formik.touched as any)['vehicleSingular']}
          secondTouched={(formik.touched as any)['vehiclePlural']}
          firstErrors={(formik.errors as any)['vehicleSingular']}
          secondErrors={(formik.errors as any)['vehiclePlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.vehicle)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.vehicle])}`}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Project'}
          firstName={'projectSingular'}
          secondName={'projectPlural'}
          firstValue={(formik.values as any)['projectSingular']}
          secondValue={(formik.values as any)['projectPlural']}
          firstTouched={(formik.touched as any)['projectSingular']}
          secondTouched={(formik.touched as any)['projectPlural']}
          firstErrors={(formik.errors as any)['projectSingular']}
          secondErrors={(formik.errors as any)['projectPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.project)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.project])}`}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Supplier'}
          firstName={'supplierSingular'}
          secondName={'supplierPlural'}
          firstValue={(formik.values as any)['supplierSingular']}
          secondValue={(formik.values as any)['supplierPlural']}
          firstTouched={(formik.touched as any)['supplierSingular']}
          secondTouched={(formik.touched as any)['supplierPlural']}
          firstErrors={(formik.errors as any)['supplierSingular']}
          secondErrors={(formik.errors as any)['supplierPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.supplier)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.supplier])}`}
        />
        <FormTextDoubleInput
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          hideEditIcon={false}
          disabled={false}
          title={'Product'}
          firstName={'productSingular'}
          secondName={'productPlural'}
          firstValue={(formik.values as any)['productSingular']}
          secondValue={(formik.values as any)['productPlural']}
          firstTouched={(formik.touched as any)['productSingular']}
          secondTouched={(formik.touched as any)['productPlural']}
          firstErrors={(formik.errors as any)['productSingular']}
          secondErrors={(formik.errors as any)['productPlural']}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          onCancelWithName={handleCancelWithName}
          isBottomRow={true}
          firstPlaceholder={`Singular e.g., ${capitaliseFirst(defaultSingularMap.product)}`}
          secondPlaceholder={`Plural e.g., ${capitaliseFirst(defaultPluralMap[defaultSingularMap.product])}`}
        />
      </Box>
    )
  }